export type VehicleDescriptorMatch = 'plate' | 'vin' | 'make' | 'model' | 'color' | 'category'

export type VehicleIdentityExpectation = {
  plateNumber?: string | null
  vin?: string | null
  make?: string | null
  model?: string | null
  color?: string | null
  category?: string | null
}

export type VehicleIdentityExtraction = {
  plateNumber?: string | null
  vin?: string | null
  chassisNumber?: string | null
  make?: string | null
  model?: string | null
  color?: string | null
  vehicleClass?: string | null
}

export type VehicleIdentityMismatch = {
  field: VehicleDescriptorMatch
  expected: string
  actual: string
}

export type VehicleIdentityEvaluation = {
  matches: VehicleDescriptorMatch[]
  mismatches: VehicleIdentityMismatch[]
  normalizedPlate: string
  normalizedVin: string
  hasStrongIdentityMatch: boolean
  descriptorMatchCount: number
}

const GENERIC_CAR_CATEGORIES = new Set(['car', 'sedan', 'compact', 'luxury', 'sports', 'suv'])

/** Normalize Salvadoran vehicle plates and strip tarjeta year suffixes. */
export function normalizeVehiclePlate(value: string | null | undefined): string {
  const raw = (value ?? '').toUpperCase().trim()
  const explicitYearSuffix = raw.match(/^([A-Z0-9]{4,10})\s*[-–—]\s*(?:19|20)\d{2}$/)
  if (explicitYearSuffix?.[1]) return explicitYearSuffix[1].replace(/[\s-]/g, '')

  const compact = raw.replace(/[\s-]/g, '')
  const compactYearSuffix = compact.match(/^([A-Z]{1,3}\d{2,5}[A-Z]{1,3})(?:19|20)\d{2}$/)
  if (compactYearSuffix?.[1]) return compactYearSuffix[1]

  const broadYearSuffix = compact.match(/^([A-Z0-9]{4,8})(?:19|20)\d{2}$/)
  const candidate = broadYearSuffix?.[1]
  if (candidate && /[A-Z]/.test(candidate) && /\d/.test(candidate)) return candidate

  return compact
}

/**
 * Stable lookup identity for Salvadoran plates. Some legacy imports omitted
 * the private-vehicle `P` prefix (for example `985380` vs `P985380`). Keep
 * the display/canonical value intact, but make those two representations
 * resolve to the same vehicle everywhere we compare or search identifiers.
 */
export function vehiclePlateLookupKey(value: string | null | undefined): string {
  const normalized = normalizeVehiclePlate(value)
  return /^P\d{4,8}$/.test(normalized) ? normalized.slice(1) : normalized
}

/** A VIN is exactly 17 characters — vehicle.schema.ts enforces the same. */
export const VEHICLE_VIN_LENGTH = 17

export function normalizeVehicleVin(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[\s-]/g, '')
}

export function vehicleDescriptorMatches(
  actual: string | null | undefined,
  expected: string | null | undefined,
): boolean {
  const left = normalizeVehicleDescriptor(actual)
  const right = normalizeVehicleDescriptor(expected)
  if (!left || !right) return true
  const compactLeft = left.replace(/\s+/g, '')
  const compactRight = right.replace(/\s+/g, '')
  return (
    left.includes(right) ||
    right.includes(left) ||
    compactLeft.includes(compactRight) ||
    compactRight.includes(compactLeft)
  )
}

export function vehicleCategoryMatches(
  actual: string | null | undefined,
  expected: string | null | undefined,
): boolean {
  const left = normalizeVehicleCategory(actual)
  const right = normalizeVehicleCategory(expected)
  if (!left || !right) return true
  if (left === right) return true
  if (left === 'car' && GENERIC_CAR_CATEGORIES.has(right)) return true
  if (right === 'car' && GENERIC_CAR_CATEGORIES.has(left)) return true
  return false
}

export function evaluateVehicleIdentity(
  extracted: VehicleIdentityExtraction,
  expected: VehicleIdentityExpectation,
): VehicleIdentityEvaluation {
  const matches: VehicleDescriptorMatch[] = []
  const mismatches: VehicleIdentityMismatch[] = []
  const extractedPlate = normalizeVehiclePlate(extracted.plateNumber)
  const expectedPlate = normalizeVehiclePlate(expected.plateNumber)
  const extractedVin = normalizeVehicleVin(extracted.vin || extracted.chassisNumber)
  const expectedVin = normalizeVehicleVin(expected.vin)

  compareLoosePlate(extractedPlate, expectedPlate, matches, mismatches)
  compareLooseVin(extractedVin, expectedVin, matches, mismatches)
  compareDescriptor('make', extracted.make, expected.make, matches, mismatches)
  compareDescriptor('model', extracted.model, expected.model, matches, mismatches)
  compareDescriptor('color', extracted.color, expected.color, matches, mismatches)
  compareCategory('category', extracted.vehicleClass, expected.category, matches, mismatches)

  const hasStrongIdentityMatch = matches.includes('plate') || matches.includes('vin')
  const descriptorMatchCount = matches.filter((match) =>
    ['make', 'model', 'color', 'category'].includes(match),
  ).length

  return {
    matches,
    mismatches,
    normalizedPlate: extractedPlate,
    normalizedVin: extractedVin,
    hasStrongIdentityMatch,
    descriptorMatchCount,
  }
}

function compareLoosePlate(
  actual: string,
  expected: string,
  matches: VehicleDescriptorMatch[],
  mismatches: VehicleIdentityMismatch[],
) {
  if (!actual || !expected) return
  if (platesLooselyMatch(actual, expected)) {
    matches.push('plate')
  } else {
    mismatches.push({ field: 'plate', actual, expected })
  }
}

function compareLooseVin(
  actual: string,
  expected: string,
  matches: VehicleDescriptorMatch[],
  mismatches: VehicleIdentityMismatch[],
) {
  if (!actual || !expected) return
  if (vinsLooselyMatch(actual, expected)) {
    matches.push('vin')
  } else {
    mismatches.push({ field: 'vin', actual, expected })
  }
}

/** One-character OCR slip on a Salvadoran plate (P86E44 vs P85E44, O/0, I/1). */
export function platesLooselyMatch(
  actual: string | null | undefined,
  expected: string | null | undefined,
): boolean {
  const left = vehiclePlateLookupKey(actual)
  const right = vehiclePlateLookupKey(expected)
  if (!left || !right) return false
  if (left === right) return true
  if (Math.abs(left.length - right.length) > 1) return false
  const foldedLeft = foldOcrConfusions(left)
  const foldedRight = foldOcrConfusions(right)
  return (
    foldedLeft === foldedRight ||
    levenshtein(left, right) <= 1 ||
    levenshtein(foldedLeft, foldedRight) <= 1
  )
}

/** Chassis and VIN are the same field on many Salvadoran cards; OCR slips 1–2 chars. */
export function vinsLooselyMatch(
  actual: string | null | undefined,
  expected: string | null | undefined,
): boolean {
  const left = normalizeVehicleVin(actual)
  const right = normalizeVehicleVin(expected)
  if (!left || !right) return false
  if (left === right) return true
  if (left.includes(right) || right.includes(left)) return true
  const foldedLeft = foldOcrConfusions(left)
  const foldedRight = foldOcrConfusions(right)
  if (foldedLeft === foldedRight) return true
  if (left.length >= 11 && right.length >= 11) {
    return levenshtein(foldedLeft, foldedRight) <= 2
  }
  return false
}

export function rescuePlateFromText(
  rawText: string | null | undefined,
  expectedPlate?: string | null,
): string {
  const text = (rawText ?? '').toUpperCase()
  if (!text) return ''
  const expected = normalizeVehiclePlate(expectedPlate)
  if (expected) {
    const compact = text.replace(/[^A-Z0-9]/g, '')
    if (compact.includes(expected) || platesLooselyMatch(scanSalvadoranPlate(text), expected)) {
      return expected
    }
    const near = compact.match(new RegExp(expected[0] + '[A-Z0-9]{' + (expected.length - 1) + '}'))
    if (near?.[0] && platesLooselyMatch(near[0], expected)) return expected
  }
  return normalizeVehiclePlate(scanSalvadoranPlate(text))
}

export function rescueVinFromText(
  rawText: string | null | undefined,
  expectedVin?: string | null,
): string {
  const text = (rawText ?? '').toUpperCase()
  if (!text) return ''
  const expected = normalizeVehicleVin(expectedVin)
  const compact = text.replace(/[^A-Z0-9]/g, '')
  if (expected && compact.includes(expected)) return expected
  const candidate = text.match(/\b([A-HJ-NPR-Z0-9]{11,17})\b/)?.[1] ?? ''
  if (expected && vinsLooselyMatch(candidate, expected)) return expected
  return normalizeVehicleVin(candidate)
}

export type TarjetaAcceptanceInput = {
  documentType: 'tarjeta_circulacion_front' | 'tarjeta_circulacion_back'
  documentSide: string
  confidence: number
  expiryDate: string | null
  rawText: string
  identity: VehicleIdentityEvaluation
  expectedPlate: string
  expectedVin: string
}

export type TarjetaAcceptance = {
  ok: boolean
  reasons: string[]
}

/**
 * Accept every tarjeta we can reasonably confirm is this car's current card.
 *
 * Hard rejects only: expired front, a completely different plate/VIN, or a
 * photo that is clearly the other side with none of this side's fields.
 * Glare, a 1-character OCR slip, VIN printed as "N/T DE FABRICA", unknown
 * side, and soft make/model/color drift do not block.
 */
export function decideTarjetaAcceptance(input: TarjetaAcceptanceInput): TarjetaAcceptance {
  const reasons: string[] = []
  const plateMismatch = input.identity.mismatches.find((item) => item.field === 'plate')
  const vinMismatch = input.identity.mismatches.find((item) => item.field === 'vin')
  const inferredSide = inferTarjetaSide(input.documentSide, input.rawText, input.identity)
  const sawNothing =
    input.confidence < 0.3 &&
    !input.identity.normalizedPlate &&
    !input.identity.normalizedVin &&
    !input.expiryDate &&
    !looksLikeTarjeta(input.rawText)

  if (sawNothing) {
    reasons.push('La tarjeta no se leyó con suficiente confianza.')
  }

  if (input.documentType === 'tarjeta_circulacion_front') {
    if (inferredSide === 'back' && !input.expiryDate && !input.identity.normalizedPlate) {
      reasons.push('La foto del frente no parece ser el frente de la tarjeta.')
    }
    if (!input.expiryDate) {
      reasons.push('La tarjeta está vencida o sin fecha vigente.')
    } else if (!isFutureISODate(input.expiryDate)) {
      reasons.push('La tarjeta está vencida o sin fecha vigente.')
    }
    if (!input.identity.hasStrongIdentityMatch && !input.identity.normalizedPlate) {
      reasons.push('No se pudo leer la placa de la tarjeta.')
    }
  } else if (
    inferredSide === 'front' &&
    !input.identity.normalizedVin &&
    !looksLikeTarjetaBack(input.rawText)
  ) {
    reasons.push('La foto del reverso no parece ser el reverso de la tarjeta.')
  }

  if (plateMismatch) {
    reasons.push('La placa de la tarjeta no coincide con el vehículo.')
  }
  if (vinMismatch) {
    reasons.push('El VIN/chasis de la tarjeta no coincide con el vehículo.')
  }

  return { ok: reasons.length === 0, reasons }
}

function inferTarjetaSide(
  declared: string,
  rawText: string,
  identity: VehicleIdentityEvaluation,
): 'front' | 'back' | 'unknown' {
  if (declared === 'front' || declared === 'back') return declared
  if (looksLikeTarjetaBack(rawText) || identity.normalizedVin) return 'back'
  if (/\bvto\b|\bvencimiento\b|tarjeta de circulacion/i.test(rawText) || identity.normalizedPlate) {
    return 'front'
  }
  return 'unknown'
}

function looksLikeTarjeta(rawText: string): boolean {
  return /tarjeta de circulaci|placa|chasis|chassis|\bvin\b|\bvto\b/i.test(rawText)
}

function looksLikeTarjetaBack(rawText: string): boolean {
  return /\bchasis\b|\bchassis\b|\bmarca\b|\bmodelo\b|\ba[nñ]o\b|\bcabina\b|\btraccion\b/i.test(
    rawText,
  )
}

function scanSalvadoranPlate(text: string): string {
  /*
   * Una placa salvadoreña SIEMPRE lleva dígitos. Sin esta condición, el reverso
   * de la tarjeta —que imprime "DOMINIO PROPIEDAD / EN CALIDAD PROPIEDAD" y no
   * trae placa— hacía que `P[A-Z0-9]{4,8}` capturara la palabra "PROPIEDAD",
   * que se comparaba contra la placa real del vehículo y la daba por no
   * coincidente. Resultado: TODO reverso de tarjeta era imposible de aprobar
   * automáticamente y el carro quedaba "En revisión" para siempre. Igual
   * capturaba "PARTICULAR" o "PESADA". Se exige al menos un dígito.
   */
  const matches = text.match(/\bP[A-Z0-9]{4,8}\b/g) ?? []
  return matches.find((candidate) => /\d/.test(candidate)) ?? ''
}

function foldOcrConfusions(value: string): string {
  return value
    .replace(/O/g, '0')
    .replace(/I/g, '1')
    .replace(/S/g, '5')
    .replace(/B/g, '8')
    .replace(/G/g, '6')
}

function levenshtein(left: string, right: string): number {
  if (left === right) return 0
  if (!left) return right.length
  if (!right) return left.length
  const rows = left.length + 1
  const cols = right.length + 1
  const grid = Array.from({ length: rows }, () => new Array<number>(cols).fill(0))
  for (let i = 0; i < rows; i += 1) grid[i]![0] = i
  for (let j = 0; j < cols; j += 1) grid[0]![j] = j
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1
      grid[i]![j] = Math.min(
        (grid[i - 1]![j] ?? 0) + 1,
        (grid[i]![j - 1] ?? 0) + 1,
        (grid[i - 1]![j - 1] ?? 0) + cost,
      )
    }
  }
  return grid[left.length]![right.length] ?? Math.max(left.length, right.length)
}

function isFutureISODate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  return value > new Date().toISOString().slice(0, 10)
}

function compareDescriptor(
  field: VehicleDescriptorMatch,
  actual: string | null | undefined,
  expected: string | null | undefined,
  matches: VehicleDescriptorMatch[],
  mismatches: VehicleIdentityMismatch[],
) {
  if (!actual || !expected) return
  if (vehicleDescriptorMatches(actual, expected)) {
    matches.push(field)
  } else {
    mismatches.push({ field, actual, expected })
  }
}

function compareCategory(
  field: VehicleDescriptorMatch,
  actual: string | null | undefined,
  expected: string | null | undefined,
  matches: VehicleDescriptorMatch[],
  mismatches: VehicleIdentityMismatch[],
) {
  if (!actual || !expected) return
  if (vehicleCategoryMatches(actual, expected)) {
    matches.push(field)
  } else {
    mismatches.push({ field, actual, expected })
  }
}

function normalizeVehicleCategory(value: string | null | undefined): string {
  const normalized = normalizeVehicleDescriptor(value)
  if (['auto', 'automovil', 'automobile', 'car', 'vehiculo', 'vehicle'].includes(normalized)) {
    return 'car'
  }
  if (['pick', 'pickup', 'pickups', 'truck'].includes(normalized)) return 'pickup'
  if (['camioneta', 'utilitario'].includes(normalized)) return 'suv'
  if (['compacto', 'hatchback'].includes(normalized)) return 'compact'
  if (['sport', 'sports', 'deportivo'].includes(normalized)) return 'sports'
  if (['luxury', 'lujo'].includes(normalized)) return 'luxury'
  if (['motorcycle', 'motocicleta'].includes(normalized)) return 'motorcycle'
  return normalized
}

function normalizeVehicleDescriptor(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(automatico|automatica|automatic|transmision|transmission|manual)\b/g, ' ')
    .replace(/\b(color|modelo|marca|tipo|clase|categoria)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
